import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, mergeProps as _mergeProps, Fragment as _Fragment, renderSlot as _renderSlot, renderList as _renderList, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "app-header" }
const _hoisted_2 = { class: "header-left" }
const _hoisted_3 = { class: "header-right" }
const _hoisted_4 = { class: "impersonate-info" }
const _hoisted_5 = { class: "from-user" }
const _hoisted_6 = { class: "to-user" }
const _hoisted_7 = { class: "biz-info" }
const _hoisted_8 = { class: "org-name" }
const _hoisted_9 = { class: "biz-name" }
const _hoisted_10 = {
  key: 2,
  class: "user-info"
}
const _hoisted_11 = { class: "header-role-badge" }
const _hoisted_12 = { class: "user-name" }
const _hoisted_13 = { class: "impersonate-menu-info" }
const _hoisted_14 = { class: "info-text" }
const _hoisted_15 = { class: "value" }
const _hoisted_16 = { class: "admin-main" }
const _hoisted_17 = { class: "exit-header-title" }
const _hoisted_18 = { class: "biz-dialog-header-title" }
const _hoisted_19 = ["disabled"]
const _hoisted_20 = {
  key: 0,
  class: "biz-gubun-list"
}
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "biz-info" }
const _hoisted_23 = { class: "member-name" }
const _hoisted_24 = { class: "member-biz" }
const _hoisted_25 = {
  key: 1,
  class: "loading-area"
}

import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import AdminNavi from './AdminNavi.vue';
import UserProfileDialog from '@/components/common/UserProfileDialog.vue';
import { ADMIN_NAVIGATION_ITEMS, MEMBER_NAVIGATION_ITEMS, type NavItem } from '@/config/navigation';

const NAV_COLLAPSE_KEY = 'caion_nav_collapsed';

export default /*@__PURE__*/_defineComponent({
  __name: 'AdminLayout',
  setup(__props) {

const store = useStore();
const router = useRouter();
const route = useRoute();

const drawer = ref(false);
const isWide = ref(typeof window !== 'undefined' ? window.innerWidth >= 1180 : true);
const navCollapsedPref = ref(
  typeof window !== 'undefined' && localStorage.getItem(NAV_COLLAPSE_KEY) === '1'
);
const exitDialog = ref(false);
const profileDialog = ref(false);

// 사업구분 관련 상태
const bizGubunDialog = ref(false);
const bizGubunList = ref<any[]>([]);
const selectedBizGubunIdx = ref<number | null>(null);
const loadingBizGubun = ref(false);
const switching = ref(false);

const currentUser = computed(() => store.getters.currentUser);
const isImpersonating = computed(() => store.getters.isImpersonating);
const originalUser = computed(() => store.getters.originalUser);
const hasMultipleBizGubun = computed(() => store.getters.hasMultipleBizGubun);
const bizGubunName = computed(() => store.getters.bizGubunName);
const bizGubunColor = computed(() => store.getters.bizGubunColor);
const roleLevel = computed(() => Number(store.getters.roleLevel || 0));

// 멤버 페이지(/m/*)인지 관리 페이지인지 판단
const isOnMemberPages = computed(() => route.path.startsWith('/m/'));
const isConsole = computed(() => !isOnMemberPages.value);
const navPermanent = computed(() => isConsole.value && isWide.value);
const navCollapsed = computed(() => navPermanent.value && navCollapsedPref.value);
const showNavToggle = computed(() => isConsole.value || !navPermanent.value);
const navToggleLabel = computed(() => {
  if (!navPermanent.value) return drawer.value ? '메뉴 닫기' : '메뉴 열기';
  return navCollapsed.value ? '메뉴 펼치기' : '메뉴 줄이기';
});
const navToggleIcon = computed(() => {
  if (!navPermanent.value) return drawer.value ? 'mdi-close' : 'mdi-menu';
  return navCollapsed.value ? 'mdi-menu' : 'mdi-menu-open';
});

const toggleNav = () => {
  if (navPermanent.value) {
    navCollapsedPref.value = !navCollapsedPref.value;
    localStorage.setItem(NAV_COLLAPSE_KEY, navCollapsedPref.value ? '1' : '0');
    return;
  }
  drawer.value = !drawer.value;
};

const syncDrawer = () => {
  if (navPermanent.value) drawer.value = true;
};

const onResize = () => {
  isWide.value = window.innerWidth >= 1180;
};

watch(navPermanent, syncDrawer);
onMounted(() => {
  window.addEventListener('resize', onResize);
  syncDrawer();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});

// 라우트 컨텍스트에 따라 네비게이션 항목 동적 선택
const activeNavItems = computed(() => {
  return isOnMemberPages.value ? MEMBER_NAVIGATION_ITEMS : ADMIN_NAVIGATION_ITEMS;
});

const filterMenuByRole = (items: NavItem[]): NavItem[] => {
  return items
    .filter((item) => {
      const minRole = item.minRole ?? 0;
      return roleLevel.value >= minRole;
    })
    .map((item) => {
      if (!item.children) return item;
      const children = filterMenuByRole(item.children);
      if (children.length === 0) return { ...item, children: [] };
      return { ...item, children };
    })
    .filter((item) => !item.children || item.children.length > 0 || !!item.to);
};

const roleFilteredNav = computed(() => filterMenuByRole(activeNavItems.value));

const filteredMenuItems = computed(() => {
  if (isOnMemberPages.value) return roleFilteredNav.value;
  return roleFilteredNav.value.filter((item) => (item.minRole ?? 0) < 80);
});

// 관리자(80~99) / 슈퍼바이저(100) — 멤버 페이지에서는 묶지 않음
const adminMenuItems = computed(() => {
  if (isOnMemberPages.value) return [];
  return roleFilteredNav.value.filter((item) => {
    const min = item.minRole ?? 0;
    return min >= 80 && min < 100;
  });
});

const supervisorMenuItems = computed(() => {
  if (isOnMemberPages.value) return [];
  return roleFilteredNav.value.filter((item) => (item.minRole ?? 0) >= 100);
});

const goHome = () => {
  const target = isOnMemberPages.value ? '/m/info' : '/dashboard';
  if (route.path !== target) router.push(target);
};

const goToMyInfo = () => {
  router.push('/m/myinfo');
};

const handleLogout = () => {
  store.dispatch('logout');
  window.location.href = '/login';
};

const exitImpersonation = () => {
  exitDialog.value = true;
};

const confirmExit = async () => {
  await store.dispatch('exitImpersonation');
  window.location.reload();
};

// 사업구분 다이얼로그 열기
const openBizGubunDialog = async () => {
  bizGubunDialog.value = true;
  loadingBizGubun.value = true;
  selectedBizGubunIdx.value = null;

  try {
    const result = await store.dispatch('fetchBizGubunList');
    if (result.success) {
      bizGubunList.value = result.data;
    }
  } catch (error) {
    console.error('사업구분 목록 조회 실패:', error);
  } finally {
    loadingBizGubun.value = false;
  }
};

// 사업구분 선택
const selectBizGubun = (member: any) => {
  if (member.idx !== currentUser.value?.idx) {
    selectedBizGubunIdx.value = member.idx;
  }
};

// 사업구분 전환 확인
const confirmBizGubunSwitch = async () => {
  if (!selectedBizGubunIdx.value) return;

  switching.value = true;

  try {
    const result = await store.dispatch('switchBizGubun', selectedBizGubunIdx.value);
    if (result.success) {
      bizGubunDialog.value = false;
      window.location.reload();
    }
  } finally {
    switching.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, {
    class: _normalizeClass(["admin-layout", { 'is-console': isConsole.value, 'nav-docked': navPermanent.value, 'nav-collapsed': navCollapsed.value }])
  }, {
    default: _withCtx(() => [
      _createElementVNode("header", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (showNavToggle.value)
            ? (_openBlock(), _createBlock(_component_v_btn, {
                key: 0,
                icon: "",
                variant: "text",
                color: "white",
                size: "small",
                class: "menu-toggle-btn",
                "aria-label": navToggleLabel.value,
                onClick: toggleNav
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(navToggleIcon.value), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["aria-label"]))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            type: "button",
            class: "app-brand app-brand-btn",
            onClick: goHome
          }, [...(_cache[9] || (_cache[9] = [
            _createElementVNode("span", { class: "app-title" }, "CAION", -1),
            _createElementVNode("span", { class: "app-tagline" }, "장기요양 통합관리", -1)
          ]))])
        ]),
        _createElementVNode("div", _hoisted_3, [
          (isImpersonating.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "impersonate-indicator",
                onClick: exitImpersonation
              }, [
                _createVNode(_component_v_icon, {
                  size: "18",
                  class: "impersonate-icon"
                }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("mdi-incognito", -1)
                  ]))]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(originalUser.value?.name), 1),
                  _cache[11] || (_cache[11] = _createElementVNode("span", { class: "arrow" }, "→", -1)),
                  _createElementVNode("span", _hoisted_6, _toDisplayString(currentUser.value?.name), 1)
                ]),
                _createVNode(_component_v_icon, {
                  size: "16",
                  class: "exit-icon"
                }, {
                  default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                    _createTextVNode("mdi-close-circle", -1)
                  ]))]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          (!isImpersonating.value && hasMultipleBizGubun.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "biz-gubun-switcher",
                onClick: openBizGubunDialog
              }, [
                _createElementVNode("div", {
                  class: "biz-color-dot",
                  style: _normalizeStyle({ backgroundColor: bizGubunColor.value })
                }, null, 4),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(currentUser.value?.name), 1),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(bizGubunName.value), 1)
                ]),
                _createVNode(_component_v_icon, {
                  size: "18",
                  color: "white",
                  class: "switch-icon"
                }, {
                  default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                    _createTextVNode("mdi-swap-horizontal", -1)
                  ]))]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          (!isImpersonating.value && !hasMultipleBizGubun.value && currentUser.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("span", _hoisted_11, _toDisplayString(currentUser.value.roleName), 1),
                _createElementVNode("span", _hoisted_12, _toDisplayString(currentUser.value.name) + "님", 1)
              ]))
            : _createCommentVNode("", true),
          _createVNode(_component_v_menu, null, {
            activator: _withCtx(({ props }) => [
              _createVNode(_component_v_btn, _mergeProps({
                icon: "",
                size: "small",
                variant: "text",
                color: "white"
              }, props), {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, null, {
                    default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                      _createTextVNode("mdi-account-circle", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }, 16)
            ]),
            default: _withCtx(() => [
              _createVNode(_component_v_list, { density: "compact" }, {
                default: _withCtx(() => [
                  (isImpersonating.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createVNode(_component_v_list_item, { class: "impersonate-menu-item" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_13, [
                              _createVNode(_component_v_icon, {
                                size: "small",
                                color: "deep-purple"
                              }, {
                                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                  _createTextVNode("mdi-incognito", -1)
                                ]))]),
                                _: 1
                              }),
                              _createElementVNode("div", _hoisted_14, [
                                _cache[16] || (_cache[16] = _createElementVNode("span", { class: "label" }, "접속 중인 계정", -1)),
                                _createElementVNode("span", _hoisted_15, _toDisplayString(currentUser.value?.name), 1)
                              ])
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_list_item, {
                          onClick: exitImpersonation,
                          class: "exit-menu-item"
                        }, {
                          prepend: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              size: "small",
                              color: "error"
                            }, {
                              default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                _createTextVNode("mdi-arrow-left-circle", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_v_list_item_title, { class: "text-error" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(originalUser.value?.name) + "(으)로 복귀", 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_divider)
                      ], 64))
                    : _createCommentVNode("", true),
                  (isOnMemberPages.value)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 1,
                        onClick: goToMyInfo
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { size: "small" }, {
                            default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                              _createTextVNode("mdi-information-outline", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                              _createTextVNode("기관 정보", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (!isImpersonating.value && !isOnMemberPages.value)
                    ? (_openBlock(), _createBlock(_component_v_list_item, {
                        key: 2,
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (profileDialog.value = true))
                      }, {
                        prepend: _withCtx(() => [
                          _createVNode(_component_v_icon, { size: "small" }, {
                            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                              _createTextVNode("mdi-account-cog-outline", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, null, {
                            default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                              _createTextVNode("내 정보", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (isOnMemberPages.value || (!isImpersonating.value && !isOnMemberPages.value))
                    ? (_openBlock(), _createBlock(_component_v_divider, { key: 3 }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_list_item, { onClick: handleLogout }, {
                    prepend: _withCtx(() => [
                      _createVNode(_component_v_icon, { size: "small" }, {
                        default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                          _createTextVNode("mdi-logout", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, null, {
                        default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                          _createTextVNode("로그아웃", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ])
      ]),
      _createVNode(AdminNavi, {
        modelValue: drawer.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((drawer).value = $event)),
        permanent: navPermanent.value,
        collapsed: navCollapsed.value,
        "menu-items": filteredMenuItems.value,
        "admin-items": adminMenuItems.value,
        "supervisor-items": supervisorMenuItems.value
      }, null, 8, ["modelValue", "permanent", "collapsed", "menu-items", "admin-items", "supervisor-items"]),
      _createElementVNode("div", _hoisted_16, [
        _renderSlot(_ctx.$slots, "default")
      ]),
      _createVNode(UserProfileDialog, {
        modelValue: profileDialog.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((profileDialog).value = $event))
      }, null, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: exitDialog.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((exitDialog).value = $event)),
        "max-width": "380px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "exit-dialog" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "exit-header" }, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_17, [
                    _createVNode(_component_v_icon, {
                      class: "mr-2",
                      color: "warning"
                    }, {
                      default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                        _createTextVNode("mdi-arrow-left-circle", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[25] || (_cache[25] = _createTextVNode(" 권한 복귀 ", -1))
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "exit-close",
                    "aria-label": "닫기",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (exitDialog.value = false))
                  }, [
                    _createVNode(_component_v_icon, { size: "18" }, {
                      default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                        _createTextVNode("mdi-close", -1)
                      ]))]),
                      _: 1
                    })
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "exit-body" }, {
                default: _withCtx(() => [
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, _toDisplayString(originalUser.value?.name), 1),
                    _cache[27] || (_cache[27] = _createTextVNode(" 계정으로 돌아가시겠습니까?", -1))
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "exit-actions" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    class: "btn-cancel",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (exitDialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                      _createTextVNode("취소", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    class: "btn-confirm",
                    onClick: confirmExit
                  }, {
                    default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                      _createTextVNode("복귀하기", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: bizGubunDialog.value,
        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((bizGubunDialog).value = $event)),
        "max-width": "420px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "biz-gubun-dialog" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "biz-dialog-header" }, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_18, [
                    _createVNode(_component_v_icon, { class: "mr-2" }, {
                      default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                        _createTextVNode("mdi-swap-horizontal-circle", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[31] || (_cache[31] = _createTextVNode(" 사업구분 전환 ", -1))
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "biz-dialog-close",
                    "aria-label": "닫기",
                    disabled: switching.value,
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (bizGubunDialog.value = false))
                  }, [
                    _createVNode(_component_v_icon, { size: "18" }, {
                      default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                        _createTextVNode("mdi-close", -1)
                      ]))]),
                      _: 1
                    })
                  ], 8, _hoisted_19)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "biz-dialog-body" }, {
                default: _withCtx(() => [
                  _cache[35] || (_cache[35] = _createElementVNode("p", { class: "biz-dialog-desc" }, "다른 사업구분으로 전환합니다.", -1)),
                  (!loadingBizGubun.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bizGubunList.value, (member) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: member.idx,
                            class: _normalizeClass(["biz-gubun-item", {
                current: member.idx === currentUser.value?.idx,
                selected: selectedBizGubunIdx.value === member.idx
              }]),
                            onClick: ($event: any) => (selectBizGubun(member))
                          }, [
                            _createElementVNode("div", {
                              class: "biz-color",
                              style: _normalizeStyle({ backgroundColor: member.bizGubunColor })
                            }, null, 4),
                            _createElementVNode("div", _hoisted_22, [
                              _createElementVNode("span", _hoisted_23, _toDisplayString(member.name), 1),
                              _createElementVNode("span", _hoisted_24, _toDisplayString(member.bizGubunName || member.bizGubun), 1)
                            ]),
                            (member.idx === currentUser.value?.idx)
                              ? (_openBlock(), _createBlock(_component_v_chip, {
                                  key: 0,
                                  size: "x-small",
                                  color: "primary",
                                  variant: "flat"
                                }, {
                                  default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                                    _createTextVNode("현재", -1)
                                  ]))]),
                                  _: 1
                                }))
                              : (selectedBizGubunIdx.value === member.idx)
                                ? (_openBlock(), _createBlock(_component_v_icon, {
                                    key: 1,
                                    color: "primary"
                                  }, {
                                    default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                                      _createTextVNode("mdi-check-circle", -1)
                                    ]))]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                          ], 10, _hoisted_21))
                        }), 128))
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_25, [
                        _createVNode(_component_v_progress_circular, {
                          indeterminate: "",
                          color: "primary",
                          size: "32"
                        })
                      ]))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "biz-dialog-actions" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    class: "btn-cancel",
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (bizGubunDialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                      _createTextVNode("취소", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    class: "btn-confirm",
                    disabled: !selectedBizGubunIdx.value || selectedBizGubunIdx.value === currentUser.value?.idx,
                    loading: switching.value,
                    onClick: confirmBizGubunSwitch
                  }, {
                    default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                      _createTextVNode(" 전환하기 ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled", "loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 3
  }, 8, ["class"]))
}
}

})